import { LaunchApiError, launchChain } from "../../../api/launchpad";
import { launchRecords, launchTx, setLaunchMetadataState, subscribeLaunchTx } from "../../../wallet/launchpad-transactions";
import type { WalletSessionController } from "../../../wallet/wallet-session";
import { attachMetadataDraft, waitForLaunchIndex } from "./metadata";

type PublishStatus = { phase: "indexing" | "attaching" | "error"; message?: string };
type LaunchRecord = NonNullable<ReturnType<typeof launchTx>>;

/** Owns off-chain attachment for the lifetime of the app, independent of routes. */
export class MetadataPublisher {
  private jobs = new Map<string, AbortController>();
  private statuses = new Map<string, PublishStatus>();
  private listeners = new Set<() => void>();
  private stopped = false;
  private revision: number;
  private identity: number;
  private unsubscribeWallet: () => void;
  private unsubscribeTransactions: () => void;

  constructor(private readonly wallet: WalletSessionController) {
    this.revision = wallet.getRevision();
    this.identity = wallet.getIdentityRevision();
    this.unsubscribeWallet = wallet.subscribe(() => {
      if (this.revision !== wallet.getRevision() || this.identity !== wallet.getIdentityRevision()) {
        this.revision = wallet.getRevision();
        this.identity = wallet.getIdentityRevision();
        this.jobs.forEach(controller => controller.abort());
        this.jobs.clear();
        this.statuses.clear();
        this.notify();
      }
      this.scan();
    });
    this.unsubscribeTransactions = subscribeLaunchTx(() => this.scan());
    this.scan();
  }

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };
  getStatus = (hash: string | undefined) => hash ? this.statuses.get(hash) : undefined;
  private notify() { this.listeners.forEach(listener => listener()); }
  private status(hash: string, status: PublishStatus) {
    this.statuses.set(hash, status);
    this.notify();
  }

  retry(scope: string) {
    const row = launchTx(scope);
    const metadata = row?.creation?.metadata;
    if (this.stopped || !row || !["confirmed", "event-error"].includes(row.state) || !row.hash || this.jobs.has(row.hash) ||
        !metadata?.draft || ["none", "published", "skipped"].includes(metadata.state) ||
        metadata.draft.creator.toLowerCase() !== row.account.toLowerCase() ||
        this.wallet.getSnapshot().chainId !== launchChain ||
        row.account.toLowerCase() !== this.wallet.getAccount()?.toLowerCase()) return;
    this.statuses.delete(row.hash);
    setLaunchMetadataState(scope, row.hash, row.token, "retry");
  }

  private scan() {
    if (this.stopped || import.meta.env.VITE_APP_MODE === "DEMO") return;
    const { account, chainId } = this.wallet.getSnapshot();
    if (!account || chainId !== launchChain) return;
    for (const row of launchRecords().values()) {
      const metadata = row.creation?.metadata;
      if (!["confirmed", "event-error"].includes(row.state) || !row.hash || row.block === undefined ||
          row.account.toLowerCase() !== account.toLowerCase() || !metadata?.draft ||
          metadata.draft.creator.toLowerCase() !== account.toLowerCase() ||
          !["pending", "unknown", "retry"].includes(metadata.state) ||
          this.jobs.has(row.hash) || this.statuses.get(row.hash)?.phase === "error") continue;
      const controller = new AbortController();
      this.jobs.set(row.hash, controller);
      this.status(row.hash, { phase: "indexing" });
      // Register ownership before any transaction-store notification can rescan.
      this.publish(row, controller).catch(() => { /* Errors are exposed by publish. */ });
    }
  }

  private async publish(row: LaunchRecord, controller: AbortController) {
    const { hash, token, block } = row;
    const draft = row.creation?.metadata?.draft;
    if (!hash || block === undefined || !draft) return;
    const signal = controller.signal;
    const revision = this.revision, identity = this.identity;
    const check = () => {
      signal.throwIfAborted();
      if (this.stopped || this.wallet.getRevision() !== revision ||
          this.wallet.getIdentityRevision() !== identity) throw new Error("Wallet context changed.");
      // A newer create may have moved this launch into session history.
      const current = [...launchRecords().values()].find(saved => saved.hash === hash && saved.token === token);
      if (!current || !["confirmed", "event-error"].includes(current.state) || current.creation?.metadata?.draft?.draftId !== draft.draftId ||
          ["none", "published", "skipped"].includes(current.creation.metadata.state)) throw new Error("The launch changed.");
    };
    let uploading = false;
    try {
      check();
      if (token) await waitForLaunchIndex(token, row.account, block, hash, signal);
      check();
      const result = await attachMetadataDraft(draft, token, hash, block, signal, check, () => {
        setLaunchMetadataState(row.scope, hash, token, "unknown");
        uploading = true;
        this.status(hash, { phase: "attaching" });
      });
      check();
      if (result.name !== row.creation?.name || result.symbol !== row.creation.symbol || result.pairId !== row.creation.pair || !result.antiSniper?.enabled) throw new Error("Attached token does not match this launch.");
      setLaunchMetadataState(row.scope, hash, token, "published", result.image ?? result.imageUrl ?? result.logoUrl, result.address);
      this.statuses.delete(hash);
      this.notify();
    } catch (cause) {
      if (signal.aborted || this.stopped) return;
      // A lost response remains unknown; retry always uses the same draft/token.
      this.status(hash, { phase: "error", message: cause instanceof Error ? cause.message : "Could not publish metadata." });
      if (!uploading || !(cause instanceof TypeError || (cause instanceof DOMException && cause.name === "TimeoutError") ||
          (cause instanceof LaunchApiError && (cause.status >= 500 || cause.status === 408)))) {
        try { setLaunchMetadataState(row.scope, hash, token, "error"); }
        catch { /* Retain the original record and expose the failure for retry. */ }
      }
    } finally {
      if (this.jobs.get(hash) === controller) this.jobs.delete(hash);
    }
  }

  dispose() {
    this.stopped = true;
    this.unsubscribeWallet();
    this.unsubscribeTransactions();
    this.jobs.forEach(controller => controller.abort());
    this.jobs.clear();
    this.listeners.clear();
  }
}
